// src/components/SettingsCentral.js

/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { db } from "../firebase";
import { tryResolveTenantId } from '../utils/tenant';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import LocationsManager from "./LocationsManager";
import DepartmentsManager from "./DepartmentsManager";
import RolesManager from "./RolesManager";
import BusinessSettings from "./BusinessSettings";

// --- Main SettingsCentral Component ---
export default function SettingsCentral() {
  const theme = useTheme();
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  // which top-level settings section is active: 'locations' or 'company'
  const [activeSection, setActiveSection] = useState('locations');
  // confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  // deletingTarget will be { type: 'location'|'department'|'role', item }
  const [deletingTarget, setDeletingTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const selectedLocation = locations.find((location) => location.id === selectedLocationId) || null;

  const locationStructureWorkspace = selectedLocationId ? (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.25, md: 3 },
        borderRadius: 5,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: alpha(theme.palette.background.paper, 0.95),
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 800,
              letterSpacing: '0.11em',
            }}
          >
            Structure Builder
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5, mb: 1, fontWeight: 800 }}>
            Location, Departments, and Roles
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 900, lineHeight: 1.65 }}>
            Build the staffing structure in order: pick a location, create the departments that belong to it, then add the roles that belong to each department.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
          <Chip
            label={`1. Location: ${selectedLocation?.name || 'Selected'}`}
            sx={{
              height: 38,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
              fontWeight: 700,
            }}
          />
          <Chip
            label="2. Departments"
            sx={{
              height: 38,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              color: 'warning.dark',
              fontWeight: 700,
            }}
          />
          <Chip
            label="3. Roles"
            sx={{
              height: 38,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: 'success.dark',
              fontWeight: 700,
            }}
          />
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) minmax(0, 1fr)' },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 4,
              borderColor: alpha(theme.palette.warning.main, 0.14),
              bgcolor: alpha(theme.palette.warning.main, 0.03),
            }}
          >
            <DepartmentsManager
              embedded
              locationName={selectedLocation?.name || ""}
              selectedLocationId={selectedLocationId}
              selectedDepartmentId={selectedDepartmentId}
              onSelectDepartment={setSelectedDepartmentId}
              onDeleteDepartment={(dept) => {
                setDeletingTarget({ type: 'department', item: dept });
                setDeleteError(null);
                setConfirmOpen(true);
              }}
            />
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 4,
              borderColor: alpha(theme.palette.success.main, 0.14),
              bgcolor: alpha(theme.palette.success.main, 0.03),
            }}
          >
            <RolesManager
              embedded
              selectedDepartmentId={selectedDepartmentId}
              selectedLocationId={selectedLocationId}
              onDeleteRole={(role) => {
                setDeletingTarget({ type: 'role', item: role });
                setDeleteError(null);
                setConfirmOpen(true);
              }}
            />
          </Paper>
        </Box>
      </Stack>
    </Paper>
  ) : null;

  // Fetch locations and auto-select first one
  useEffect(() => {
    let unsub = null;
    (async () => {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) return setLocations([]);
      const q = query(collection(db, `tenants/${tenantId}/locations`), orderBy("name"));
      unsub = onSnapshot(q, snap => {
        const locs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setLocations(locs);
        if (locs.length && !selectedLocationId) {
          setSelectedLocationId(locs[0].id);
        }
        if (!locs.length) {
          setSelectedLocationId(null);
          setSelectedDepartmentId(null);
        }
      });
    })();
    return () => { if (unsub) unsub(); };
    // eslint-disable-next-line
  }, []);

  // TODO: Add Location handler if needed
  // const handleAddLocation = async (name) => {
  //   await addDoc(collection(db, "locations"), { name });
  //   // After adding, auto-select the first location
  //   const q = query(collection(db, "locations"), orderBy("name"));
  //   const snap = await new Promise(resolve => {
  //     onSnapshot(q, resolve);
  //   });
  //   const locs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  //   if (locs.length) {
  //     setSelectedLocationId(locs[0].id);
  //   }
  // };

  // Parent-level add location handler. Accepts a name, creates the location under the tenant,
  // and selects the newly-created location.
  const handleAddLocation = async (name) => {
    if (!name || !name.trim()) return null;
    try {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Tenant not found');
      const docRef = await addDoc(collection(db, `tenants/${tenantId}/locations`), { name: name.trim() });
      if (docRef && docRef.id) {
        setSelectedLocationId(docRef.id);
      }
      return docRef;
    } catch (err) {
      console.error('Failed to add location from SettingsCentral', err);
      return null;
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeletingTarget(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) throw new Error('Tenant not found');
  if (deletingTarget.type === 'location') {
        const loc = deletingTarget.item;
  // Archive instead of deleting so historical shifts retain readable locationName
  await updateDoc(doc(db, `tenants/${tenantId}/locations/${loc.id}`), { archived: true, updatedAt: new Date() });
        if (selectedLocationId === loc.id) {
          setSelectedLocationId(null);
          setSelectedDepartmentId(null);
        }
      } else if (deletingTarget.type === 'department') {
        const dept = deletingTarget.item;
  // Archive department so historical shifts keep their deptName
  await updateDoc(doc(db, `tenants/${tenantId}/departments/${dept.id}`), { archived: true, updatedAt: new Date() });
        if (selectedDepartmentId === dept.id) setSelectedDepartmentId(null);
      } else if (deletingTarget.type === 'role') {
        const role = deletingTarget.item;
  // Archive role instead of deleting so historical shifts keep roleName
  await updateDoc(doc(db, `tenants/${tenantId}/roles/${role.id}`), { archived: true, updatedAt: new Date() });
      }
      setConfirmOpen(false);
      setDeletingTarget(null);
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };
  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 3, md: 4 }, mb: 5 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: { xs: 3, md: 4 },
          borderRadius: 5,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.08)} 100%)`,
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 800,
                letterSpacing: '0.12em',
              }}
            >
              Settings Hub
            </Typography>
            <Typography
              variant="h3"
              sx={{
                mt: 0.5,
                mb: 1,
                fontWeight: 800,
                fontSize: { xs: '2.1rem', md: '3rem' },
                lineHeight: 1.05,
              }}
            >
              Settings
            </Typography>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 860,
                color: 'text.secondary',
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              Manage store setup, scheduling rules, labor settings, and company-wide defaults from one cleaner workspace.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'inline-flex',
              flexWrap: 'wrap',
              gap: 1.25,
              p: 1,
              borderRadius: 999,
              bgcolor: alpha(theme.palette.common.white, 0.56),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              width: 'fit-content',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Button
              variant={activeSection === 'locations' ? 'contained' : 'text'}
              onClick={() => setActiveSection('locations')}
              sx={{
                px: 2.5,
                py: 1.1,
                borderRadius: 999,
                fontWeight: 700,
                boxShadow: activeSection === 'locations' ? 3 : 'none',
              }}
            >
              Location Settings
            </Button>
            <Button
              variant={activeSection === 'company' ? 'contained' : 'text'}
              onClick={() => setActiveSection('company')}
              sx={{
                px: 2.5,
                py: 1.1,
                borderRadius: 999,
                fontWeight: 700,
                boxShadow: activeSection === 'company' ? 3 : 'none',
              }}
            >
              Company Settings
            </Button>
          </Box>
        </Stack>
      </Paper>

      {activeSection === 'locations' && (
        <>
          <LocationsManager
            selectedLocationId={selectedLocationId}
            onSelectLocation={(id) => {
              setSelectedLocationId(id);
              // clear department selection when switching locations
              setSelectedDepartmentId(null);
            }}
            onDeleteLocation={(loc) => {
              setDeletingTarget({ type: 'location', item: loc });
              setDeleteError(null);
              setConfirmOpen(true);
            }}
            onAddLocation={handleAddLocation}
            childrenAfterSelector={locationStructureWorkspace}
          />

          {/* Departments and roles are location-scoped; require a selected location */}
          {!selectedLocationId ? (
            <Box sx={{ mt: 2 }}>
              <Typography color="text.secondary">Select a location to view and manage Departments and Roles.</Typography>
            </Box>
          ) : null}
        </>
      )}

      {activeSection === 'company' && (
        <>
          <Box sx={{ mt: 1 }}>
            <BusinessSettings />
          </Box>

          {/* Company-level settings */}
        </>
      )}

      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>
          {deletingTarget?.type === 'department' ? 'Delete department?' : deletingTarget?.type === 'role' ? 'Delete role?' : 'Delete location?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deletingTarget?.type === 'location' && 'Deleting a location will remove all location-specific data (departments, assignments, etc.).'}
            {deletingTarget?.type === 'department' && 'Deleting a department will remove its roles and assignments. This cannot be undone.'}
            {deletingTarget?.type === 'role' && 'Deleting a role will remove it from any employee assignments. This cannot be undone.'}
            {' '}Are you sure you want to delete <strong>{deletingTarget?.item?.name || 'this item'}</strong>?
          </DialogContentText>
          {deleteError && (
            <Box sx={{ mt: 2, color: 'error.main' }}>{deleteError}</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// ...confirmation dialog markup added below the component (rendered by the component body)
